import ApiService from './ApiService';

const STORAGE_KEY = 'disclaimer_acknowledged';
// NOTE: This flag is a UX convenience only — it has no security or compliance function.
// Accepted risk: a user can bypass the modal by setting sessionStorage directly.
// The disclaimer is informational; no legal obligation requires server-side recording.
// If compliance requirements change, replace with a server-side acknowledgment API.
export const isDisclaimerAcknowledged = (): boolean =>
  sessionStorage.getItem(STORAGE_KEY) === 'true';

export const setDisclaimerAcknowledged = (): void =>
  sessionStorage.setItem(STORAGE_KEY, 'true');

export interface Disclaimer {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface DisclaimerDto {
  id: string;
  version: number;
  title: string;
  content: string;
  effectiveAt: string;
  createdAt: string;
  updatedAt: string;
  requiresAcknowledgment: boolean;
  acknowledgedAt: string | null;
}

export interface AcknowledgeDisclaimerRequest {
  disclaimerId: string;
  accepted: boolean;
}

export interface AcknowledgeDisclaimerResponse {
  id: string;
  disclaimerId: string;
  userId: string;
  userEmail: string;
  userName: string;
  disclaimerVersion: number;
  acknowledgedAt: string;
}

/** Read-only DTO returned by GET /api/disclaimers/current — no acknowledgment state. */
export interface CurrentDisclaimerDto {
  id: string;
  version: number;
  title: string;
  content: string;
  effectiveAt: string;
}

export interface CreateDisclaimerDto {
  title: string;
  content: string;
}

export const getDisclaimer = async (signal?: AbortSignal): Promise<Disclaimer> => {
  return await ApiService.get<Disclaimer>({ url: 'Disclaimers', signal });
};

export const getActiveDisclaimer = async (signal?: AbortSignal): Promise<DisclaimerDto> => {
  return await ApiService.get<DisclaimerDto>({ url: 'disclaimers/active', signal });
};

/** Read-only fetch — no acknowledgment state, safe to call from Help/About/Settings. */
export const getCurrentDisclaimer = async (signal?: AbortSignal): Promise<CurrentDisclaimerDto> => {
  return await ApiService.get<CurrentDisclaimerDto>({ url: 'disclaimers/current', signal });
};

export const acknowledgeDisclaimer = async (
  disclaimerId: string,
  signal?: AbortSignal,
): Promise<AcknowledgeDisclaimerResponse> => {
  return await ApiService.post<AcknowledgeDisclaimerResponse, AcknowledgeDisclaimerRequest>({
    url: 'disclaimers/acknowledge',
    data: { disclaimerId, accepted: true },
    signal,
  });
};

export const publishDisclaimer = async (
  data: CreateDisclaimerDto,
  signal?: AbortSignal,
): Promise<DisclaimerDto> => {
  return await ApiService.post<DisclaimerDto, CreateDisclaimerDto>({
    url: 'disclaimers/admin',
    data,
    signal,
  });
};
